import type { ClientLocationPlan } from "api-types/locationPlans/locationPlansResponses";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import type { ClientLocationsPortalData } from "api-types/locations/locationsResponses";
import type { ClientMasterPlanHydrated } from "api-types/masterPlans/masterPlansResponses";
import Toggle from "lib/components/actions/Toggle";
import MasterPlanReviewModal from "lib/components/builders/communities/plans/modals/MasterPlanReviewModal";
import { getLocationIcon } from "lib/components/builders/getLocationIcon";
import PlanLibraryLocationModal from "lib/components/builders/plan-library/modal/PlanLibraryLocationModal";
import { MainPanelHeader } from "lib/components/procure/shared/MainPanelHeader";
import { ARCHIVE_PLAN_MESSAGE, MULTI_UNIT_DISABLED_TOOLTIP } from "lib/constants/messages";
import { getBuilderLocationsURL, getMasterPlanCommunityPlansURL, getMasterPlanHydratedURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import { archiveCommunityPlan, unarchiveCommunityPlan } from "lib/storage/locationPlanStorage";
import { postCommunityPlansBatchForMasterPlan } from "lib/storage/masterPlanStorage";
import type { FormEvent } from "react";
import { useCallback, useMemo, useState } from "react";
import Button from "ui/buttons/Button";
import ArchivePlanModal from "ui/components/ArchivePlanModal";
import Loading from "ui/components/Loading";
import type { TreeSelectNodeType } from "ui/components/TreeSelect";
import useAppContext from "ui/hooks/useAppContext";
import NestedLocationsTable from "./NestedLocationsTable";
import styles from "./PlanLibraryLocationsTab.module.scss";

/**
 * Builds a lookup map of TreeSelect nodes from location data.
 * Locations in disabledIds are marked as both selected and disabled - they represent
 * locations that already have the master plan assigned and cannot be unselected.
 *
 * @param locations - All locations from the builder
 * @param disabledIds - IDs of locations that already have the master plan
 * @returns Map of location ID to TreeSelectNode
 */
function buildNodesById(
  locations: ClientLocationWithLevel[],
  disabledIds: Set<string>,
): Record<string, TreeSelectNodeType> {
  const nodesById: Record<string, TreeSelectNodeType> = {};
  locations.forEach((l) => {
    if (!l.id) {
      return;
    }
    const isMultiUnit = l.locationLevel?.type === "multiunit";
    const alreadyHasPlan = disabledIds.has(l.id);
    const disabled = alreadyHasPlan || isMultiUnit;
    nodesById[l.id] = {
      id: l.id,
      name: l.name,
      label: l.name,
      selected: alreadyHasPlan,
      disabled,
      disabledTooltip: isMultiUnit ? MULTI_UNIT_DISABLED_TOOLTIP : undefined,
      icon: getLocationIcon(l.locationLevel?.type),
      descriptor: l.locationLevel?.type,
      children: [],
    };
  });
  return nodesById;
}

/**
 * Links TreeSelect nodes into a parent-child hierarchy.
 * Connects child nodes to their parents and returns the root-level nodes.
 *
 * @param nodesById - Map of location ID to TreeSelectNode
 * @param locations - All locations from the builder
 * @returns Array of root-level TreeSelectNodes (nodes without parents)
 */
function linkTree(
  nodesById: Record<string, TreeSelectNodeType>,
  locations: ClientLocationWithLevel[],
): TreeSelectNodeType[] {
  const treeRoots: TreeSelectNodeType[] = [];
  locations.forEach((l) => {
    if (!l.id) {
      return;
    }
    const parentId = l.parentId ?? undefined;
    if (parentId && nodesById[parentId]) {
      nodesById[parentId].children.push(nodesById[l.id]);
    } else {
      treeRoots.push(nodesById[l.id]);
    }
  });
  return treeRoots;
}

/**
 * Recursively sorts TreeSelect nodes alphabetically by name.
 * Mutates the nodes array and their children arrays in place.
 *
 * @param nodes - Array of TreeSelectNodes to sort
 */
function sortTreeInPlace(nodes: TreeSelectNodeType[]): void {
  nodes.sort((a, b) => a.name.localeCompare(b.name));
  nodes.forEach((n) => {
    if (n.children.length > 0) {
      sortTreeInPlace(n.children);
    }
  });
}

/**
 * Groups locations by their parent ID for rendering the nested table.
 * Locations without a parent are grouped under the "ROOT" key.
 * Used to efficiently look up children when rendering the table hierarchy.
 *
 * @param filtered - Locations that have the master plan assigned
 * @returns Map of parent ID to array of child locations
 */
function groupByParent(filtered: ClientLocationWithLevel[]): Map<string, ClientLocationWithLevel[]> {
  const byParent = new Map<string, ClientLocationWithLevel[]>();
  filtered.forEach((l) => {
    const k = l.parentId ?? "ROOT";
    const existing = byParent.get(k);
    if (existing !== undefined) {
      existing.push(l);
    } else {
      byParent.set(k, [l]);
    }
  });
  return byParent;
}

export default function PlanLibraryLocationsTab() {
  const { builderId, masterPlanId } = useAppRouterParams();
  const { setUserNotification } = useAppContext();
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const { data, loading } = useFetch<ClientLocationsPortalData>(
    builderId ? getBuilderLocationsURL({ builderId }) : "",
    [builderId],
  );
  const locations = data?.locations ?? [];
  const [modalStep, setModalStep] = useState<"none" | "select" | "review">("none");
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [selectedLeafIds, setSelectedLeafIds] = useState<string[]>([]);
  const [archiveModalState, setArchiveModalState] = useState<{
    locationPlanId: string;
    locationId: string;
    isUnarchive?: boolean;
  } | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);

  const { data: masterPlan } = useFetch<ClientMasterPlanHydrated>(
    builderId && masterPlanId ? getMasterPlanHydratedURL({ builderId, masterPlanId }) : "",
    [builderId, masterPlanId],
  );

  const { data: communityPlans, mutate: mutateCommunityPlans } = useFetch<ClientLocationPlan[]>(
    builderId && masterPlanId
      ? getMasterPlanCommunityPlansURL({ builderId, masterPlanId, includeArchived: showArchived })
      : "",
    [builderId, masterPlanId],
  );

  const handleArchiveLocationPlan = useCallback(
    async (e: FormEvent<HTMLFormElement>, locationPlanId: string, locationId: string, isUnarchive = false) => {
      e.preventDefault();
      setIsArchiving(true);

      const action = isUnarchive ? "unarchive" : "archive";
      const actionPast = isUnarchive ? "unarchived" : "archived";

      if (!builderId) {
        setUserNotification({
          messageType: "error",
          message: `Unable to ${action} plan: builder ID is missing.`,
        });
        setArchiveModalState(null);
        setIsArchiving(false);
        return;
      }

      const [, error] = isUnarchive
        ? await unarchiveCommunityPlan(builderId, locationId, locationPlanId)
        : await archiveCommunityPlan(builderId, locationId, locationPlanId);

      if (error) {
        setUserNotification(error);
        setIsArchiving(false);
        return;
      }

      setUserNotification({
        messageType: "success",
        message: `Location plan ${actionPast} successfully.`,
      });
      setArchiveModalState(null);
      setIsArchiving(false);

      try {
        await mutateCommunityPlans(undefined, { revalidate: true });
      } catch (refetchError) {
        setUserNotification({
          messageType: "warning",
          message: `Plan ${actionPast} successfully, but failed to refresh the list. Please reload the page.`,
        });
      }
    },
    [builderId, mutateCommunityPlans, setUserNotification],
  );

  const { existingLocationIds, filteredLocations, disabledIds } = useMemo(() => {
    const existingIds = (communityPlans ?? [])
      .map((cp) => cp.locationId)
      .filter((id): id is string => id !== null && id !== undefined);

    const existingIdsSet = new Set(existingIds);
    const filtered = locations.filter((l) => existingIdsSet.has(l.id));
    const disabled = new Set<string>(filtered.map((l) => l.id).filter((id): id is string => !!id));

    return { existingLocationIds: existingIds, filteredLocations: filtered, disabledIds: disabled };
  }, [communityPlans, locations]);

  const { childrenByParent, rootLocations } = useMemo(() => {
    const grouped = groupByParent(filteredLocations);
    const roots = (grouped.get("ROOT") ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));
    return { childrenByParent: grouped, rootLocations: roots };
  }, [filteredLocations]);

  const treeData = useMemo(() => {
    const nodesById = buildNodesById(locations, disabledIds);
    const roots = linkTree(nodesById, locations);
    sortTreeInPlace(roots);
    return roots;
  }, [locations, disabledIds]);

  const planIdByLocationId = useMemo(() => {
    const map = new Map<string, string>();
    (communityPlans ?? []).forEach((cp) => {
      if (cp.locationId) {
        map.set(cp.locationId, cp.id);
      }
    });
    return map;
  }, [communityPlans]);

  const resetModalState = useCallback(() => {
    setModalStep("none");
    setSelectedLocationIds([]);
    setSelectedLeafIds([]);
  }, []);

  const newLeafSelections = useMemo(() => {
    const existingSet = new Set(existingLocationIds);
    return selectedLeafIds.filter((id) => !existingSet.has(id));
  }, [selectedLeafIds, existingLocationIds]);

  const newSelections = useMemo(() => {
    const currentSelections = selectedLocationIds.length > 0 ? selectedLocationIds : existingLocationIds;
    const existingSet = new Set(existingLocationIds);
    return currentSelections.filter((id) => !existingSet.has(id));
  }, [selectedLocationIds, existingLocationIds]);

  const handleSubmit = useCallback(
    async (setIsSaving?: (saving: boolean) => void) => {
      if (newLeafSelections.length === 0) {
        setIsSaving?.(false);
        setModalStep("none");
        return;
      }

      if (!builderId || !masterPlanId) {
        return;
      }

      if (!masterPlan?.name) {
        setUserNotification({
          messageType: "error",
          message: "Master plan name is required",
        });
        setIsSaving?.(false);
        return;
      }

      try {
        const [result, error] = await postCommunityPlansBatchForMasterPlan(builderId, masterPlanId, {
          communityIds: newLeafSelections,
          name: masterPlan.name,
        });

        if (error) {
          setUserNotification({
            messageType: "error",
            message: error.message || "Failed to add plan to locations",
          });
          setIsSaving?.(false);
          return;
        }

        setUserNotification({
          messageType: "success",
          message: `Added plan to ${result?.length ?? 0} location(s)`,
        });

        await mutateCommunityPlans();

        setIsSaving?.(false);
        resetModalState();
      } catch (err) {
        console.error("Error adding plan to locations:", err);
        setUserNotification({
          messageType: "error",
          message: "An unexpected error occurred",
        });
        setIsSaving?.(false);
      }
    },
    [
      newLeafSelections,
      builderId,
      masterPlanId,
      masterPlan?.name,
      setUserNotification,
      mutateCommunityPlans,
      resetModalState,
    ],
  );

  return (
    <>
      <div className={styles.wrapper}>
        <MainPanelHeader
          className={styles.header}
          header="Manage Location Plans"
          subheader={`View and manage which locations have the ${masterPlan?.name ?? ""}`}
        >
          <div className={styles.headerActions}>
            <label className={styles.toggleArchived}>
              <Toggle isTrue={showArchived} onChange={setShowArchived} variants="small" /> Include Archived
            </label>
            <Button
              btnSize="medium"
              btnStyle="secondary"
              className={styles.locationButton}
              onClick={() => setModalStep("select")}
            >
              Add to Locations
            </Button>
          </div>
        </MainPanelHeader>
        {loading ? (
          <Loading />
        ) : (
          <NestedLocationsTable
            data={rootLocations}
            masterPlanName={masterPlan?.name ?? ""}
            childrenByParent={childrenByParent}
            builderId={builderId}
            planIdByLocationId={planIdByLocationId}
            communityPlans={communityPlans}
            masterPlan={masterPlan}
            onArchivePlan={(locationPlanId, locationId) => setArchiveModalState({ locationPlanId, locationId })}
            onUnarchivePlan={(locationPlanId, locationId) =>
              setArchiveModalState({ locationPlanId, locationId, isUnarchive: true })
            }
            showArchived={showArchived}
          />
        )}
      </div>
      {modalStep === "select" && (
        <PlanLibraryLocationModal
          modalProps={{
            open: true,
            onClose: resetModalState,
            size: "medium",
            scrollModal: true,
          }}
          treeData={treeData}
          selectedIds={selectedLocationIds.length > 0 ? selectedLocationIds : existingLocationIds}
          onSelectionChange={setSelectedLocationIds}
          onLeafSelectionChange={setSelectedLeafIds}
          existingCount={existingLocationIds.length}
          onReview={() => setModalStep("review")}
        />
      )}
      {modalStep === "review" && (
        <MasterPlanReviewModal
          modalProps={{
            open: true,
            onClose: resetModalState,
          }}
          onBack={() => setModalStep("select")}
          onSubmit={handleSubmit}
          planName={masterPlan?.name ?? ""}
          basePrice={masterPlan?.basePrice}
          treeData={treeData}
          selectedIds={newSelections}
          useNewLocationsLabel
          title="Review New Locations"
          submitButtonText="Add Plan"
        />
      )}
      {archiveModalState && (
        <ArchivePlanModal
          open
          onClose={() => setArchiveModalState(null)}
          onConfirm={(e) =>
            handleArchiveLocationPlan(
              e,
              archiveModalState.locationPlanId,
              archiveModalState.locationId,
              archiveModalState.isUnarchive,
            )
          }
          planDetail="location plan"
          planName={communityPlans?.find((cp) => cp.id === archiveModalState.locationPlanId)?.name}
          message={archiveModalState.isUnarchive ? undefined : ARCHIVE_PLAN_MESSAGE}
          isArchiving={isArchiving}
          isUnarchive={archiveModalState.isUnarchive}
        />
      )}
    </>
  );
}
